import React from 'react';
import { Box, Chip, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import FolderIcon from '@mui/icons-material/Folder';
import StarIcon from '@mui/icons-material/Star';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import ThemeSwitch from './ThemeSwitch';

export const SIDEBAR_WIDTH = 248;

const SYNC_LABELS = {
    saved: { icon: <CloudDoneIcon fontSize="small" color="success" />, text: 'Tersimpan di Discord' },
    pending: { icon: <CloudSyncIcon fontSize="small" color="info" />, text: 'Menyimpan perubahan...' },
    error: { icon: <CloudOffIcon fontSize="small" color="warning" />, text: 'Belum tersimpan, bot tidak bisa dihubungi. Dicoba lagi otomatis.' },
    auth: { icon: <CloudOffIcon fontSize="small" color="error" />, text: 'Sesi login habis, login ulang.' },
};

// Sidebar ala Google Drive: navigasi (File Saya, Berbintang, Terbaru,
// Trash), riwayat versi, status penyimpanan, enkripsi, tema, dan logout.
function Sidebar({
    view, onNavHome, onNavStarred, onNavRecent, onNavTrash, onOpenHistory, trashCount, storageUsed, syncStatus,
    encEnabled, onEncryptionClick, onOpenSettings, theme, setTheme, onLogout, onNavAnime = null,
}) {
    const sync = SYNC_LABELS[syncStatus] || null;
    return (
        <Box
            sx={{
                width: SIDEBAR_WIDTH,
                flexShrink: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRight: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                overflowY: 'auto',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 2.5 }}>
                <CloudQueueIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Disbox</Typography>
            </Box>

            <List sx={{ px: 1.5 }}>
                <ListItemButton selected={view === 'files'} onClick={onNavHome} sx={{ mb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><FolderIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="File Saya" />
                </ListItemButton>
                <ListItemButton selected={view === 'starred'} onClick={onNavStarred} sx={{ mb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><StarIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Berbintang" />
                </ListItemButton>
                <ListItemButton selected={view === 'recent'} onClick={onNavRecent} sx={{ mb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><ScheduleIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Terbaru" />
                </ListItemButton>
                <ListItemButton selected={view === 'trash'} onClick={onNavTrash} sx={{ mb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><DeleteOutlineIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Trash" />
                    {trashCount > 0 && <Chip label={trashCount} size="small" sx={{ height: 20 }} />}
                </ListItemButton>
                <ListItemButton onClick={onOpenHistory}>
                    <ListItemIcon sx={{ minWidth: 36 }}><HistoryIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Riwayat versi" />
                </ListItemButton>
                {onNavAnime && (
                    <ListItemButton onClick={onNavAnime} sx={{ mt: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}><LiveTvIcon fontSize="small" sx={{ color: '#ff7a1a' }} /></ListItemIcon>
                        <ListItemText primary="Anime" />
                    </ListItemButton>
                )}
            </List>

            <Box sx={{ flex: 1 }} />

            <Divider />
            <Box sx={{ px: 2.5, py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Terpakai</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{storageUsed}</Typography>
                {sync && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                        {sync.icon}
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>{sync.text}</Typography>
                    </Box>
                )}
            </Box>
            <Divider />

            <List sx={{ px: 1.5 }}>
                <ListItemButton onClick={onEncryptionClick}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        {encEnabled ? <LockIcon fontSize="small" color="success" /> : <LockOpenIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText primary="Encryption" secondary={encEnabled ? "Aktif" : "Mati"} />
                </ListItemButton>
                <ListItemButton onClick={onOpenSettings}>
                    <ListItemIcon sx={{ minWidth: 36 }}><SettingsIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Setelan" />
                </ListItemButton>
            </List>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
                <ThemeSwitch theme={theme} setTheme={setTheme} />
                <Tooltip title="Logout">
                    <IconButton onClick={onLogout} size="small">
                        <LogoutIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
}

export default Sidebar;
